import { Component, Input, OnDestroy, OnInit, forwardRef, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormControlTextbox } from '../../controls';
import { LastFieldInteractionService } from '../../services';
import { PlatformName, PlatformProvider, StorageService, ToastDuration, ToastService, fileDataTypesMapUI, sanitizePositionProperty } from '@shared';
import { GeolocationService } from '@domain/geolocation';

@Component({
	selector: 'app-gps-input',
	templateUrl: './gps-input.component.html',
	styleUrls: ['./gps-input.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => GpsInputComponent),
			multi: true
		}
	]
})
export class GpsInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@Input() public dynamicFormControl: FormControlTextbox;
	@Input() public form: UntypedFormGroup;
	@Input() public isDebounceApplied: boolean;
	public fieldValue: number | any;
	public fieldHasFocus: boolean;
	public isFormFilledByGps: boolean;
	public lastFieldSubscription: Subscription;

	public constructor(
		private lastFieldInteractionService: LastFieldInteractionService,
		private geolocationService: GeolocationService,
		private platformProvider: PlatformProvider,
		private cdr: ChangeDetectorRef,
		private storeService: StorageService,
		private toastService: ToastService,
	) {}

	public async ngOnInit() {
		if (this.platformProvider.getPlatform() === PlatformName.IOS) {
			this.dynamicFormControl.type = fileDataTypesMapUI.CHAR;
		}

		this.fieldValue =
			this.dynamicFormControl.value !== undefined ? Number(this.dynamicFormControl.value) : undefined;
		this.lastFieldSubscription = this.lastFieldInteractionService.lastAccessField$.subscribe(
			(fieldId: string | null) => (this.fieldHasFocus = fieldId === this.dynamicFormControl.key)
		);

		this.isFormFilledByGps = await this.geolocationService.getCurrentGPSStatus();
		this.cdr.detectChanges(); 
	}

	public writeValue(value: number): void {
		if (value) {
			this.fieldValue = value;
		}
	}

	public registerOnChange(fn: any): void {
		this.propagateChange = fn;
	}

	public registerOnTouched(): void {}

	public updateFieldHasFocus(): void {
		this.lastFieldInteractionService.setFieldId(this.dynamicFormControl.key);
	}

	public ngOnDestroy(): void {
		this.lastFieldSubscription.unsubscribe();
	}

	public fieldValueChanged(e: any): void {
		this.fieldValue = sanitizePositionProperty(e.detail.value, this.platformProvider.getPlatform());
		this.propagateChange(this.fieldValue);
	}

	public async getCurrentLocation(): Promise<void> {
		if (this.platformProvider.getPlatform() === PlatformName.WEB) {
			await this.toastService.presentError("GPS cannot be enabled on the web platform.", ToastDuration.Fast)
			return;
        }
		const value = await this.geolocationService.getCurrentGPSStatus();
		
		if(value) {
			const position = await this.geolocationService.getCurrentPositionWithoutPermissionCheck();
			const positionPropertyValue = this.geolocationService.mapWorkfilePropertyToPositionProperty(
				this.dynamicFormControl.key
			);
			this.fieldValue = position[positionPropertyValue];
	
			this.form.get(this.dynamicFormControl.key)?.setValue(this.fieldValue);
			this.form.get(this.dynamicFormControl.key)?.markAsDirty();
			this.form.get(this.dynamicFormControl.key)?.markAsTouched();
	
			this.propagateChange(this.fieldValue);
	
			this.isFormFilledByGps = true;
			this.cdr.detectChanges(); 
		} else {
			await this.toastService.presentError("Please enable your GPS in setting", ToastDuration.Fast)
		}
	}

	/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
	private propagateChange = (_: any) => {};
}
